import type { ProductListItem } from '@providers/ProductListProvider';
import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';

import type { ProductSwatchProps } from '@/features/filter-product-list/product-list/product-item/ProductSwatch';

export type ProductSwatchesPropsMapSettings = {
  [swatches: `swatches.${number}`]: Pick<
    ProductSwatchProps,
    'justifyContent' | 'options' | 'onSelect' | 'onHover' | 'itemShape'
  > & {
    changeProductImageOn?: 'click' | 'hover';
  };
};

export type ProductSwatchesProps = Pick<
  ProductListItem,
  'id' | 'original_images' | 'images_info' | 'options_with_values'
>;

export const useConnectProductSwatches = (
  place: 'img' | 'info',
  { id, original_images, images_info, options_with_values: swatches }: ProductSwatchesProps
) => {
  const settings = useProductItemThemeSettings();
  const productElmSettings =
    place === 'img' ? settings.productImg.elements : settings.productInfo?.elements;

  const productSwatchesProps: ProductSwatchesPropsMapSettings = {};
  if (productElmSettings) {
    const justifyContentPositionMap: Record<string, string> = {
      left: 'flex-start',
      center: 'center',
      right: 'flex-end',
    };

    // Maximum 3 swatches
    [1, 2, 3].forEach((index) => {
      const swatchesSettings = productElmSettings[`swatches.${index}`];
      const options = swatches?.find(
        (item) => (item.original_name?.toLowerCase() || item.name) === swatchesSettings?.swatchType
      );
      if (swatchesSettings && options) {
        const position = place === 'info' ? settings.productInfo?.textAlign : undefined;

        productSwatchesProps[`swatches.${index}`] = {
          justifyContent: position && justifyContentPositionMap[position],
          options: options.values.map((value) => ({
            value: value.origin_title || value.title,
            label: value.title,
            id: `product-swatch-${id}-${value.title}`,
            labelAs: swatchesSettings.swatchBy,
            displayType: swatchesSettings.shape,
            showOptionValueOnHovering: swatchesSettings.showOptionValueOnHovering,
            image: value.image
              ? (original_images ? original_images : images_info)?.find(
                  (item) => item.position === value.image
                )?.src
              : undefined,
          })),
          itemShape: swatchesSettings.shape,
          changeProductImageOn: swatchesSettings.changeProductImageOn,
        };
      }
    });
  }

  return {
    props: productSwatchesProps,
  };
};
